import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";

import TableContainer from "@mui/material/TableContainer";
import { useContext, useEffect, useState } from "react";
import { API_URL } from "../../utils/config";
import { v4 as uuidv4 } from "uuid";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import moment from "moment";
import DataTable from "../../pages/History/DataTable";
import { TableContext } from "../../pages/Dashboard";
import SendPTCReport from "../SendPTCReport";
import SendMonthlySnapshotReport from "../SendMonthlySnapshotReport";

const HistroyTable = () => {
  const { filters, loading, filterLoading, handleChangeData, handleChangeLoading } = useContext(TableContext);
  const headCells = [
    "utility_code",
    "utility_name",
    "state_id",
    "commodity",
    "class",
    "start_date",
    "end_date",
    "ptc_rate",
    "uom"
  ];
  const [sourceData, setSourceData] = useState([]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isShowMonthly, setIsShowMonthly] = useState(true);

  const handleSearch = (filters, _rows) => {
    const _intialRows = _rows ? _rows : sourceData;

    let dateFilteredData = _intialRows;
    const dateFilter = filters.find(filter => filter.name === "date");
    if (dateFilter && dateFilter.value[0] && dateFilter.value[1]) {
      const [queryStartDate, queryEndDate] = dateFilter.value;
      dateFilteredData = dateFilteredData.filter(item => {
        if (!item.start_date || !item.end_date) {
          return true;
        }
        
        const dataStartDate = moment(item.start_date, "YYYY/MM/DD");
        const dataEndDate = moment(item.end_date, "YYYY/MM/DD");
        const queryStart = moment(queryStartDate);
        const queryEnd = moment(queryEndDate);
        return dataEndDate.isSameOrAfter(queryStart, 'day') && 
               dataStartDate.isSameOrBefore(queryEnd, 'day');
      });
    }

    const filteredData = dateFilteredData.filter((item) =>
      filters.every((filter) => {
        if (filter.name === "date") return true;
        return filter.value !== "" ? item[filter.name] === filter.value : true;
      })
    );
    return [...filteredData];
  };


  useEffect(() => {
    const getData = async () => {
      try {
        handleChangeLoading(true);
        await getCurrentUser();
        const session = await fetchAuthSession();
        if (!session.tokens?.accessToken) {
          throw new Error("No access token available");
        }
        const token = session.tokens.accessToken.toString();
        const res = await fetch(`${API_URL}/ptc/history`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        const _rows = data.response.map((row) => ({ id: uuidv4(), ...row }));
        formataArray(_rows)
        handleChangeData(_rows)
        // setIntialRows(_rows);
        // setRows(_rows);
        handleChangeLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        handleChangeLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (!sourceData.length) return;

    let baseData = sourceData;

    const filtered = handleSearch(filters, baseData);

    const displayData = isShowMonthly
      ? filtered
      : getMergedData(filtered);

    setRows(displayData);

  }, [isShowMonthly, sourceData, filters]);

  const getMergedData = (data) => {
    const mergedArray = [];
    const mergedGroups = {};

    data.forEach(item => {
      const groupKey = `${item.utility_name}|${item.utility_code}|${item.class}`;
      if (!mergedGroups[groupKey]) mergedGroups[groupKey] = [];
      mergedGroups[groupKey].push(item);
    });

    Object.values(mergedGroups).forEach(group => {
      group.sort((a, b) => 
        moment(a.start_date, "YYYY/MM/DD") - moment(b.start_date, "YYYY/MM/DD")
      );

      let currentMerge = null;

      group.forEach(item => {
        if (!currentMerge) {
          currentMerge = { ...item };
        } else if (
          currentMerge.ptc_rate === item.ptc_rate &&
          moment(currentMerge.end_date, "YYYY/MM/DD").add(1, 'day').isSameOrAfter(
            moment(item.start_date, "YYYY/MM/DD")
          )
        ) {
          currentMerge.end_date = item.end_date;
        } else {
          mergedArray.push(currentMerge);
          currentMerge = { ...item };
        }
      });

      if (currentMerge) {
        mergedArray.push(currentMerge);
      }
    });

    return mergedArray;
  };

  const convertToCSV = (data) => {
    const csvRows = [];
    const headerCellNames = [
      "Utility Code",
      "Utility Name",
      "State",
      "Commodity",
      "Class",
      "Start Date",
      "End Date",
      "PTC Rates",
      "UOM"
    ];
    csvRows.push(headerCellNames.join(","));

    // Add data rows
    data.forEach((row) => {
      const values = headCells.map((header) => JSON.stringify(row[header]));
      csvRows.push(values.join(","));
    });
    return csvRows.join("\n");
  };

  const downloadCSV = (data, filename = "table_data.csv") => {
    let _data = data;
    if (selected.length > 0) {
      _data = rows
        .map((row) => {
          const i = selected.find((select) => select === row.id);
          if (i) {
            return row;
          }
          return null;
        })
        .filter((row) => row !== null);
    }

    const csvData = convertToCSV(_data);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formataArray = (originalArray) => {
    const today = new Date();
    const todayFormatted = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

    const getMonthEndDate = (dateString) => {
      const dateStr = dateString.replace(/-/g, '/');
      const [year, month] = dateStr.split('/').map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      return `${year}/${String(month).padStart(2, '0')}/${String(lastDay).padStart(2, '0')}`;
    };

    const preprocessedArray = originalArray.map(item => {
      let sortDate;
      if (item.start_time) {
        const [year, month, day] = item.start_time.substring(0, 10).split('-');
        sortDate = `${year}/${month}/${day}`;
      } else if (item.last_updated) {
        sortDate = item.last_updated;
      } else {
        sortDate = todayFormatted;
      }

      return {
        ...item,
        sortDate
      };
    });

    preprocessedArray.sort((a, b) => {
      if (a.utility_name !== b.utility_name) return a.utility_name.localeCompare(b.utility_name);
      if (a.utility_code !== b.utility_code) return a.utility_code.localeCompare(b.utility_code);
      if (a.class !== b.class) return a.class.localeCompare(b.class);
      return new Date(a.sortDate) - new Date(b.sortDate);
    });

    const fullArray = [];
    const groups = {};

    preprocessedArray.forEach(item => {
      const key = `${item.state_id}|${item.utility_name}|${item.utility_code}|${item.class}|${item.commodity}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    Object.values(groups).forEach(group => {
      group.sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));

      group.forEach((current, index) => {
        const newItem = { ...current };
        
        if (current.start_time) {
          const [year, month, day] = current.start_time.substring(0, 10).split('-');
          newItem.start_date = `${year}/${month}/${day}`;
        } else if (current.last_updated) {
          newItem.start_date = current.last_updated;
        } else {
          newItem.start_date = todayFormatted;
        }

        if (current.end_time) {
          const [year, month, day] = current.end_time.substring(0, 10).split('-');
          newItem.end_date = `${year}/${month}/${day}`;
        } else if (index < group.length - 1) {
          const nextItem = group[index + 1];
          if (current.last_updated) {
            newItem.end_date = getMonthEndDate(current.last_updated);
          } else if (nextItem.start_time) {
            const [year, month, day] = nextItem.start_time.substring(0, 10).split('-');
            newItem.end_date = `${year}/${month}/${day}`;
          } else if (nextItem.last_updated) {
            newItem.end_date = nextItem.last_updated;
          } else {
            newItem.end_date = todayFormatted;
          }
        } else {
          newItem.end_date = todayFormatted;
        }
        delete newItem.sortDate;

        fullArray.push(newItem);
      });
    });
    setSourceData(fullArray);
  }

  console.log(rows)

  return (
    <div className="w-full pt-10 flex justify-center">
      <div className="w-[92%]  bg-white">
        <div className="table-header-div">
          <SendPTCReport />
          <SendMonthlySnapshotReport />
          <Button variant="contained" className="sm:!mx-6" onClick={() => downloadCSV(rows)}>
            Download List
          </Button>
          <div className="ml-6 sm:mr-6">
            <Select
              labelId="view-select-label"
              id="view-select"
              value={isShowMonthly ? "monthly" : "collapsed"}
              onChange={(e) => {
                setIsShowMonthly(e.target.value === "monthly" ? true : false)
              }}
            >
              <MenuItem value="monthly">Monthly View</MenuItem>
              <MenuItem value="collapsed">Collapsed View</MenuItem>
            </Select>
          </div>
        </div>

        <Box className="mt-10">
          <Paper>
            {
              loading || filterLoading ?
                <div style={{textAlign:"center"}}>
                  <CircularProgress color="inherit" style={{ margin:"80px 0"}} />
                </div>
                :
                <TableContainer>
                  <DataTable
                    rows={rows}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </TableContainer>
            }

          </Paper>
        </Box>
      </div>
    </div>
  );
};
export default HistroyTable;
